import React, { useEffect, useState } from "react";
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useConvexAuth, useMutation } from "convex/react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../../convex/_generated/api";
import { AdminRoutes, MainRoutes } from "../../../../shared/models";
import { Button } from "../../../../catalyst-components/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../catalyst-components/table";
import { Alert, AlertActions, AlertDescription, AlertTitle } from "../../../../catalyst-components/alert";
import { ConfirmationDialog } from "../../../../catalyst-components/confirmation-dialog";
import { Id } from "../../../../convex/_generated/dataModel";
import { Field, Label } from "../../../../catalyst-components/fieldset";
import { Input } from "../../../../catalyst-components/input";
import { AssistantTypeDialog } from "./assistant-type-dialog";

export const AssistantTypes: React.FC = () => {
    const assistantTypes = useQuery(api.assistant_types.list) || [];
    const { isAuthenticated } = useConvexAuth();
    const create = useMutation(api.assistant_types.create);
    const remove = useMutation(api.assistant_types.remove);
    const update = useMutation(api.assistant_types.update);
    
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [success, setSuccess] = useState(false);
    const [newType, setNewType] = useState({ name: '', description: '', required: false });
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        type?: { _id: Id<"assistant_types">; name: string };
    }>({ open: false });
    const [editDialog, setEditDialog] = useState<{
        open: boolean;
        type?: typeof assistantTypes[0];
    }>({ open: false });


    const navigate = useNavigate();
    
    useEffect(() => {
        if (!isAuthenticated) {
            navigate(MainRoutes.Landing);
        }
    }, [isAuthenticated]);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await create(newType);
            setAlertMessage('Assistant type created successfully!');
            setShowAlert(true);
            setSuccess(true);
            setNewType({ name: '', description: '', required: false });
        } catch (error) {
            setAlertMessage('Error creating assistant type. Please try again.');
            setShowAlert(true);
            setSuccess(false);
        }
    };

    const handleDelete = async () => {
        if (!confirmDialog.type) return;
        
        try {
            await remove({ id: confirmDialog.type._id });
            setAlertMessage('Assistant type deleted successfully!');
            setShowAlert(true);
            setSuccess(true);
            setConfirmDialog({ open: false });
        } catch (error) {
            setAlertMessage('Error deleting assistant type. Please try again.');
            setShowAlert(true);
            setSuccess(false);
        }
    };

    const handleEdit = async (data: {
        name: string;
        description: string;
        required: boolean;
    }) => {
        if (!editDialog.type) return;
        
        try {
            await update({
                id: editDialog.type._id,
                ...data
            });
            setAlertMessage('Assistant type updated successfully!');
            setShowAlert(true);
            setSuccess(true);
            setEditDialog({ open: false });
        } catch (error) {
            setAlertMessage('Error updating assistant type. Please try again.');
            setShowAlert(true);
            setSuccess(false);
        }
    };

    return (
        <section className="min-h-screen bg-background pt-24 px-6 flex flex-col justify-start">
            <h1 className="text-3xl font-bold mb-4">Assistant Types</h1>
            <div className="flex flex-row gap-4 mb-6">
                <Button color="light" onClick={() => navigate('../')}>
                    Back to admin
                </Button>
            </div>
            
            <form onSubmit={handleCreate} className="mb-8">
                <div className="flex gap-4 items-end">
                    <Field className="w-64">
                        <Label>Name</Label>
                        <Input
                            required
                            value={newType.name}
                            onChange={(e) => setNewType(prev => ({ ...prev, name: e.target.value }))}
                        />
                    </Field>
                    <Field className="w-96">
                        <Label>Description</Label>
                        <Input
                            value={newType.description || ''}
                            onChange={(e) => setNewType(prev => ({ ...prev, description: e.target.value }))}
                        />
                    </Field>
                    <Field className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={newType.required}
                            onChange={(e) => setNewType(prev => ({ ...prev, required: e.target.checked }))}
                        />
                        <Label>Required</Label>
                    </Field>
                    <Button color="light" type="submit">Add Type</Button>
                </div>
            </form>

            <Table>
                <TableHead>
                    <TableRow>
                        <TableHeader>Name</TableHeader>
                        <TableHeader>Description</TableHeader>
                        <TableHeader>Required</TableHeader>
                        <TableHeader>Actions</TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {assistantTypes.map(type => (
                        <TableRow key={type._id}>
                            <TableCell>{type.name}</TableCell>
                            <TableCell>{type.description}</TableCell>
                            <TableCell>{type.required ? 'Yes' : 'No'}</TableCell>
                            <TableCell>
                                <div className="flex gap-2">
                                    <Button
                                        color="light"
                                        onClick={() => setEditDialog({ 
                                            open: true, 
                                            type: type
                                        })}
                                    >
                                        Edit
                                    </Button>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <ConfirmationDialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ open: false })}
                onConfirm={handleDelete}
                isCritical={true}
                title="Delete Assistant Type"
                description={`Are you sure you want to delete ${confirmDialog.type?.name}?`}
                confirmText="Delete"
                cancelText="Cancel"
            />

            <AssistantTypeDialog
                open={editDialog.open}
                onClose={() => setEditDialog({ open: false })}
                onSubmit={handleEdit}
                initialData={editDialog.type}
                mode="edit"
            />

            <Alert open={showAlert} onClose={() => setShowAlert(false)} size="lg">
                <AlertTitle>Notification</AlertTitle>
                <AlertDescription>{alertMessage}</AlertDescription>
                <AlertActions>
                    <Button plain onClick={() => setShowAlert(false)}>
                        Ok
                    </Button>
                </AlertActions>
            </Alert>
        </section>
    );
}; 